import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Check, ArrowRight, Sparkles, Crown, Building2,
  Users, CreditCard, Zap, ChevronDown, ChevronUp,
  Monitor, MessageSquare, Target, TrendingUp, Bot,
  Send, Percent, Clock, BarChart3, Search, Cpu,
  Headphones, Megaphone, LineChart, Settings, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';
const COOP_FUNDING_URL = 'https://www.oneforcelive.com/path-to-funding';

const INCLUDED_SYSTEMS = [
  'AI Horse-Powered Workspace\u2122',
  'AGI Workhorse\u2122',
  'AI Horse-Powered CRM\u2122',
  'AI Horse-Powered POD\u2122',
  'AI Horse-Powered Directory\u2122',
  'AI Horse-Powered Ad Manager\u2122',
  'AI Horse-Powered Bot\u2122',
];

const AI_CAPABILITIES = [
  {
    title: 'AI Marketing',
    description: 'AI-powered content creation, social media management, email campaigns, and brand storytelling.',
    icon: Megaphone,
  },
  {
    title: 'Lead Generation',
    description: 'Automated lead capture funnels, forms, landing pages, and intelligent prospect scoring.',
    icon: Users,
  },
  {
    title: 'Sales Automation',
    description: 'AI follow-ups, pipeline management, appointment booking, and deal tracking on autopilot.',
    icon: TrendingUp,
  },
  {
    title: 'Advertising Campaigns',
    description: 'Meta, Google, and programmatic ad campaigns managed by AI with human expert oversight.',
    icon: Target,
  },
  {
    title: 'SEO Optimization',
    description: 'AI-driven keyword research, content optimization, local SEO, and ranking automation.',
    icon: Search,
  },
  {
    title: 'Workflow Automation',
    description: 'Custom automations, CRM workflows, task routing, and operational efficiency powered by AI agents.',
    icon: Settings,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [showEnterprise, setShowEnterprise] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png" 
                alt="AI Horse-Powered" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" className="hidden md:block">
                <Button variant="ghost" className="text-gray-300 hover:text-white">
                  Directory
                </Button>
              </a>
              <Link to="/login">
                <Button variant="ghost" className="text-gray-300 hover:text-white">
                  Enter
                </Button>
              </Link>
              <a href={COOP_FUNDING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-primary hover:bg-primary/90 text-black font-bold">
                  Get Approved
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6">
              <Cpu className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">One Platform. One Partnership.</span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Launch Your <span className="text-primary">AI Horse-Powered Systems&#8482;</span>
            </h1>
            
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Deploy AI assistants, AI agents, automation systems, and human experts to run your 
              marketing, operations, and customer growth inside one powerful platform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Pricing Card */}
      <section className="pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative rounded-2xl border-2 border-primary/50 bg-gradient-to-b from-primary/10 via-primary/5 to-transparent overflow-hidden"
          >
            {/* Badge */}
            <div className="flex justify-center pt-6">
              <div className="px-5 py-1.5 bg-primary text-black text-xs font-bold rounded-full shadow-lg shadow-primary/30">
                <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" /> PARTNER PLAN
              </div>
            </div>

            <div className="p-6 lg:p-10">
              {/* Plan Name */}
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                  AI Horse-Powered Systems&#8482; Partner Plan
                </h2>
              </div>

              {/* Pricing */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-10">
                <div className="text-center">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Startup Fee</p>
                  <p className="text-5xl font-heading font-bold text-primary">$0</p>
                </div>
                <div className="hidden sm:block w-px h-16 bg-white/10"></div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Monthly Platform</p>
                  <p className="text-5xl font-heading font-bold text-white">$997<span className="text-lg text-gray-400 font-normal">/mo</span></p>
                </div>
              </div>

              {/* Included Systems */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-white uppercase tracking-wide mb-4">Included Systems</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {INCLUDED_SYSTEMS.map((system) => (
                    <div key={system} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white/5 border border-white/10">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm text-white font-medium">{system}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Advertising Management */}
              <div className="mb-8 p-5 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Percent className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold text-white">Advertising Management</h3>
                </div>
                <p className="text-sm text-gray-400 mb-3">
                  Advertising campaigns include a <span className="text-white font-semibold">15% media management fee</span> applied to advertising spend.
                </p>
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-xs text-gray-300">
                    <span className="text-primary font-medium">Example:</span> $5,000 ad spend &rarr; $750 management fee
                  </p>
                </div>
              </div>

              {/* Optional: Slack Huddles */}
              <div className="mb-8 p-5 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Headphones className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-sm font-semibold text-white">Optional Support</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">OPTIONAL</span>
                </div>
                <p className="text-xs text-gray-400 mb-3 font-medium">Slack Strategy Huddles</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { time: '15 min', price: '$30' },
                    { time: '30 min', price: '$60' },
                    { time: '1 hour', price: '$120' },
                  ].map((s) => (
                    <div key={s.time} className="text-center p-3 rounded-lg bg-white/5 border border-white/10">
                      <p className="text-xs text-gray-400">{s.time}</p>
                      <p className="text-lg font-heading font-bold text-white">{s.price}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optional: Dedicated VA */}
              <div className="mb-8 p-5 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-white">Optional Dedicated Workspace Operator</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">OPTIONAL</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                    <p className="text-xs text-gray-400 mb-1">Part-Time Virtual Assistant</p>
                    <p className="text-2xl font-heading font-bold text-white">$288<span className="text-sm text-gray-400 font-normal">/week</span></p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                    <p className="text-xs text-gray-400 mb-1">Full-Time Virtual Assistant</p>
                    <p className="text-2xl font-heading font-bold text-white">$438<span className="text-sm text-gray-400 font-normal">/week</span></p>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Each VA manages the client workspace and is supported by the AI Horse-Powered POD&#8482; team of subject-matter experts.
                </p>
              </div>

              {/* 30-Day Free Trial Badge */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400 font-medium">30-Day Free Trial — No Credit Card Required</span>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <a href="https://www.aihpower.com/pricing" target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="w-full sm:w-auto px-10 py-6 text-base bg-primary hover:bg-primary/90 text-black font-bold shadow-lg shadow-primary/25"
                    data-testid="launch-workspace-btn"
                  >
                    Launch Your AI Horse-Powered Systems&#8482; <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Co-Op Partner Pricing Notice */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-green-500/40 bg-gradient-to-br from-green-500/10 to-emerald-500/5 p-8"
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-7 h-7 text-green-400" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-white">Co-Op Partner Pricing</h2>
              </div>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              The <span className="text-green-400 font-semibold">$0 startup fee</span> and <span className="text-green-400 font-semibold">$997/month</span> pricing 
              is available to businesses approved for the <span className="text-white font-semibold">$120,000 Business Co-Op Grant Program</span>.
            </p>
            <p className="text-gray-400 mb-8">
              This program helps qualified businesses deploy AI workforce technology and advanced marketing systems.
            </p>

            <a href={COOP_FUNDING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold px-8 py-5 text-base shadow-lg shadow-green-500/25"
                data-testid="coop-apply-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Get Approved in 3 Minutes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Enterprise Partnership */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-7 h-7 text-gray-300" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white">Enterprise Partnership</h2>
                  <p className="text-sm text-gray-400">Without Co-Op Funding</p>
                </div>
              </div>
              <button 
                onClick={() => setShowEnterprise(!showEnterprise)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {showEnterprise ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
              </button>
            </div>

            <p className="text-gray-400 mb-4">
              Businesses not participating in the Co-Op program may still deploy the full 
              AI Horse-Powered Systems&#8482; platform through a standard enterprise partnership.
            </p>

            {showEnterprise && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="pt-6 border-t border-white/10"
              >
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="p-5 rounded-xl bg-white/5 border border-white/10 text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Partnership Enablement Fee</p>
                    <p className="text-3xl font-heading font-bold text-white">$20,000</p>
                    <p className="text-xs text-gray-500 mt-1">one-time</p>
                  </div>
                  <div className="p-5 rounded-xl bg-white/5 border border-white/10 text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Monthly Platform</p>
                    <p className="text-3xl font-heading font-bold text-white">$11,997<span className="text-sm text-gray-400 font-normal">/mo</span></p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Includes all 7 platform systems, advertising management, and full workspace access.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* What Your AI Workforce Can Do */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              What Your AI Workforce <span className="text-primary">Can Do</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Your workspace comes loaded with AI agents and human experts that handle every major 
              function of your business growth engine.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {AI_CAPABILITIES.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-primary/30 hover:bg-primary/5 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{cap.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{cap.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Deploy Your AI Workforce?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Join businesses scaling with AI assistants, automation systems, and expert support 
              inside one powerful workspace.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://www.aihpower.com/pricing" target="_blank" rel="noopener noreferrer">
                <Button 
                  className="px-8 py-5 text-base bg-primary hover:bg-primary/90 text-black font-bold shadow-lg shadow-primary/25"
                  data-testid="final-launch-btn"
                >
                  Launch Your AI Horse-Powered Systems&#8482; <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href={COOP_FUNDING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline"
                  className="px-8 py-5 text-base border-green-500/50 text-green-400 hover:bg-green-500/10 font-bold"
                  data-testid="final-coop-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Apply for Co-Op Funding
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>&copy; 2025 AI Horse-Powered. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
